/**
 * API数据适配器工具
 *
 * @description 提供统一的API响应数据适配功能，解决PaginatedData<T>与ProTable期望格式不匹配的问题
 * @author Cross-Border ERP Team
 * @version 1.0.0
 */

import type {
  ApiResponse,
  PaginatedData,
  PaginationInfo,
} from '../services/typings';

/**
 * ProTable期望的数据格式
 */
export interface ProTableData<T = any> {
  /** 数据数组 */
  data: T[];
  /** 操作是否成功 */
  success: boolean;
  /** 总记录数 */
  total: number;
}

/**
 * 统一的ProTable数据适配器
 *
 * @description 将Laravel后端的分页响应格式适配为ProTable期望的格式
 * @template T 列表项的类型
 * @param response Laravel后端的API响应
 * @returns ProTable期望的数据格式
 *
 * @example
 * ```typescript
 * // 在ProTable的request函数中使用
 * request={async (params, sort) => {
 *   const response = await getCredentials(params);
 *   return adaptProTableData(response);
 * }}
 * ```
 */
export function adaptProTableData<T = any>(
  response: ApiResponse<PaginatedData<T>>,
): ProTableData<T> {
  return {
    data: response.data?.items || [],
    success: response.success,
    total: response.data?.pagination?.total || 0,
  };
}

/**
 * 数组数据提取器
 *
 * @description 从Laravel后端的分页响应中提取数据数组，用于状态管理
 * @template T 列表项的类型
 * @param response Laravel后端的API响应
 * @returns 数据数组
 *
 * @example
 * ```typescript
 * // 在状态管理中使用
 * const response = await getCredentials(params);
 * const credentialsList = extractItems(response);
 * setCredentials(credentialsList);
 * ```
 */
export function extractItems<T = any>(
  response: ApiResponse<PaginatedData<T>>,
): T[] {
  return response.data?.items || [];
}

/**
 * 分页信息提取器
 *
 * @description 从Laravel后端的分页响应中提取分页信息
 * @template T 列表项的类型
 * @param response Laravel后端的API响应
 * @returns 分页信息对象，如果没有分页信息则返回null
 *
 * @example
 * ```typescript
 * const response = await getCredentials(params);
 * const pagination = extractPagination(response);
 * if (pagination) {
 *   console.log(`总共 ${pagination.total} 条记录`);
 * }
 * ```
 */
export function extractPagination<T = any>(
  response: ApiResponse<PaginatedData<T>>,
): PaginationInfo | null {
  return response.data?.pagination || null;
}

/**
 * 检查响应是否包含分页数据
 *
 * @description 判断API响应是否为分页格式
 * @template T 列表项的类型
 * @param response API响应
 * @returns 是否为分页响应
 *
 * @example
 * ```typescript
 * const response = await getCredentials(params);
 * if (isPaginatedResponse(response)) {
 *   const total = response.data.pagination.total;
 *   console.log(`总共 ${total} 条记录`);
 * }
 * ```
 */
export function isPaginatedResponse<T = any>(
  response: ApiResponse<any>,
): response is ApiResponse<PaginatedData<T>> {
  return (
    response.data &&
    typeof response.data === 'object' &&
    'items' in response.data &&
    Array.isArray(response.data.items)
  );
}

/**
 * 安全的数据提取器
 *
 * @description 安全地从API响应中提取数据，自动判断是否为分页格式
 * @template T 列表项的类型
 * @param response API响应
 * @returns 数据数组
 *
 * @example
 * ```typescript
 * // 自动适配分页和非分页响应
 * const response = await getCredentials(params);
 * const items = safeExtractItems(response);
 * setCredentials(items);
 * ```
 */
export function safeExtractItems<T = any>(response: ApiResponse<any>): T[] {
  if (isPaginatedResponse<T>(response)) {
    return response.data.items || [];
  }

  // 如果不是分页响应，尝试直接返回data
  if (Array.isArray(response.data)) {
    return response.data;
  }

  // 如果data不是数组，返回空数组
  return [];
}

/**
 * 创建ProTable请求函数的工厂函数
 *
 * @description 创建标准化的ProTable请求函数，自动处理数据适配
 * @template T 列表项的类型
 * @template P 请求参数的类型
 * @param apiFunction API调用函数
 * @returns ProTable请求函数
 *
 * @example
 * ```typescript
 * // 创建标准化的请求函数
 * const credentialsRequest = createProTableRequest(getCredentials);
 *
 * // 在ProTable中使用
 * <ProTable
 *   request={credentialsRequest}
 *   // ... 其他配置
 * />
 * ```
 */
export function createProTableRequest<T = any, P = any>(
  apiFunction: (params: P) => Promise<ApiResponse<PaginatedData<T>>>,
) {
  return async (params: P & { current?: number; pageSize?: number }) => {
    try {
      const response = await apiFunction(params);
      return adaptProTableData(response);
    } catch (error) {
      console.error('[API Adapter] ProTable request failed:', error);
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };
}

/**
 * 批量数据适配器
 *
 * @description 适配批量操作的响应数据
 * @template T 操作结果的类型
 * @param response 批量操作的API响应
 * @returns 适配后的批量操作结果
 */
export function adaptBatchResponse<T = any>(
  response: ApiResponse<T>,
): { success: boolean; data: T; message: string } {
  return {
    success: response.success,
    data: response.data,
    message: response.message,
  };
}

/**
 * 错误信息提取器
 *
 * @description 从API响应中提取错误信息
 * @param response API响应
 * @returns 错误信息字符串
 */
export function extractErrorMessage(response: ApiResponse<any>): string {
  if (response.success) {
    return '';
  }

  // 优先返回具体的错误信息
  if (response.errors) {
    const errorMessages = Object.values(response.errors).flat();
    if (errorMessages.length > 0) {
      return errorMessages.join('; ');
    }
  }

  // 返回通用错误消息
  return response.message || '操作失败';
}

/**
 * 响应数据验证器
 *
 * @description 验证API响应数据的完整性
 * @param response API响应
 * @returns 验证结果
 */
export function validateResponse(response: any): response is ApiResponse<any> {
  return (
    response &&
    typeof response === 'object' &&
    typeof response.success === 'boolean' &&
    typeof response.message === 'string' &&
    'data' in response
  );
}
